/**
 * NotificationSettings Component
 * Allows users to enable/disable push notifications and manage club-specific subscriptions
 * Updated for proper contrast on light backgrounds
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Bell, BellOff, AlertCircle, CheckCircle, Loader2, X, Building2 } from 'lucide-react';
import { usePushNotifications, isPushSupported, getNotificationPermission } from '../hooks/usePushNotifications';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { safeFetch } from '../utils/safeFetch';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function NotificationSettings({ authToken, compact = false }) {
  const {
    isSubscribed,
    isLoading,
    error,
    permission,
    isSupported,
    toggleSubscription,
    checkSubscriptionStatus
  } = usePushNotifications(authToken);

  const [subscribedClubs, setSubscribedClubs] = useState([]);
  const [clubsLoading, setClubsLoading] = useState(false);

  // Fetch subscribed clubs when subscribed
  useEffect(() => {
    if (isSubscribed && authToken) {
      fetchSubscribedClubs();
    } else {
      setSubscribedClubs([]);
    }
  }, [isSubscribed, authToken]);

  const fetchSubscribedClubs = useCallback(async () => {
    if (!authToken) return;
    
    setClubsLoading(true);
    try {
      // Get subscription status with club IDs
      const statusRes = await safeFetch(`${API_URL}/api/push/status`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      const clubIds = statusRes.data?.subscription_details?.subscribed_clubs || [];
      
      if (clubIds.length > 0) {
        // Fetch club names for the subscribed clubs
        const clubsRes = await safeFetch(`${API_URL}/api/clubs`);
        if (clubsRes.data) {
          const clubs = clubsRes.data.filter(c => clubIds.includes(c.club_id));
          setSubscribedClubs(clubs);
        }
      } else {
        setSubscribedClubs([]);
      }
    } catch (err) {
      console.error('Error fetching subscribed clubs:', err);
    } finally {
      setClubsLoading(false);
    }
  }, [authToken]);

  const unsubscribeFromClub = async (clubId, clubName) => {
    try {
      const response = await safeFetch(`${API_URL}/api/push/unsubscribe-from-club/${clubId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (response.data?.success) {
        setSubscribedClubs(prev => prev.filter(c => c.club_id !== clubId));
        toast.success(`Unsubscribed from ${clubName}`);
      }
    } catch (err) {
      toast.error('Failed to unsubscribe');
    }
  };

  // Not supported
  if (!isSupported) {
    if (compact) return null;
    
    return (
      <Alert variant="default" className="bg-slate-100 border-slate-300">
        <AlertCircle className="h-4 w-4 text-slate-500" />
        <AlertDescription className="text-slate-600">
          Push notifications are not supported in this browser.
        </AlertDescription>
      </Alert>
    );
  }

  // Permission denied
  if (permission === 'denied') {
    if (compact) return null;
    
    return (
      <Alert variant="destructive" className="bg-red-50 border-red-200">
        <AlertCircle className="h-4 w-4 text-red-600" />
        <AlertDescription className="text-red-700">
          Notifications are blocked. Please enable them in your browser settings to receive alerts.
        </AlertDescription>
      </Alert>
    );
  }

  // Compact mode (for headers/sidebars)
  if (compact) {
    return (
      <Button
        variant={isSubscribed ? "ghost" : "outline"}
        size="sm"
        onClick={toggleSubscription}
        disabled={isLoading}
        className={`gap-2 ${isSubscribed ? 'text-green-600' : 'text-slate-600'}`}
        data-testid="notification-toggle-compact"
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : isSubscribed ? (
          <Bell className="h-4 w-4 fill-current" />
        ) : (
          <BellOff className="h-4 w-4" />
        )}
        <span className="sr-only md:not-sr-only">
          {isSubscribed ? 'Notifications On' : 'Notifications Off'}
        </span>
      </Button>
    );
  }

  // Full card view - Light theme compatible
  return (
    <Card className="bg-white border-slate-200 shadow-sm" data-testid="notification-settings-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg text-slate-900 flex items-center gap-2">
          <Bell className="h-5 w-5 text-violet-600" />
          Push Notifications
        </CardTitle>
        <CardDescription className="text-slate-500">
          Get notified about DJ requests, Bingo games, and event reminders
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive" className="bg-red-50 border-red-200">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-700">{error}</AlertDescription>
          </Alert>
        )}
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isSubscribed ? (
              <>
                <div className="p-2 rounded-full bg-green-100">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">Notifications Enabled</p>
                  <p className="text-xs text-slate-500">You'll receive alerts from subscribed clubs</p>
                </div>
              </>
            ) : (
              <>
                <div className="p-2 rounded-full bg-slate-100">
                  <BellOff className="h-5 w-5 text-slate-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">Notifications Disabled</p>
                  <p className="text-xs text-slate-500">Enable to receive DJ and event alerts</p>
                </div>
              </>
            )}
          </div>
          
          <Button
            variant={isSubscribed ? "outline" : "default"}
            size="sm"
            onClick={toggleSubscription}
            disabled={isLoading}
            className={isSubscribed ? "border-slate-300 text-slate-700 hover:bg-slate-100" : "bg-violet-600 hover:bg-violet-700 text-white"}
            data-testid="notification-toggle-btn"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            {isSubscribed ? 'Disable' : 'Enable'}
          </Button>
        </div>
        
        {!isSubscribed && permission === 'default' && (
          <p className="text-xs text-slate-500">
            Your browser will ask for permission when you enable notifications.
          </p>
        )}

        {/* Subscribed Clubs Section */}
        {isSubscribed && (
          <div className="pt-4 border-t border-slate-200">
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="h-4 w-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Club Subscriptions</span>
            </div>
            
            {clubsLoading ? (
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading...
              </div>
            ) : subscribedClubs.length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs text-slate-500 mb-2">
                  You'll receive notifications from these clubs:
                </p>
                <div className="flex flex-wrap gap-2">
                  {subscribedClubs.map(club => (
                    <Badge 
                      key={club.club_id}
                      variant="secondary" 
                      className="bg-violet-100 text-violet-700 border-violet-200 pl-3 pr-1 py-1 flex items-center gap-1"
                    >
                      {club.name}
                      <button
                        onClick={() => unsubscribeFromClub(club.club_id, club.name)}
                        className="ml-1 p-0.5 rounded-full hover:bg-violet-200 transition-colors"
                        title={`Unsubscribe from ${club.name}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500">
                No club subscriptions yet. Visit a club's page and click the bell icon to subscribe to their notifications.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default NotificationSettings;
